// src/pages/LearnTopic.js
// Learn topic + article page — mirrors PlaybookTopic pattern.
// Routes: /learn/:topicSlug and /learn/:topicSlug/:articleSlug

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { getTopicBySlug } from '../data/learnContent';
import { sanitizeHtml } from '../utils/sanitize';
import { trackEvent } from '../utils/analytics';
import VideoPlaceholder from '../components/business-playbook/VideoPlaceholder';

const getReadTime = (content) => {
  if (!content) return 1;
  const text = content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ');
  return Math.max(1, Math.ceil(text.split(/\s+/).length / 200));
};

const LearnTopic = () => {
  const { topicSlug, articleSlug } = useParams();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const topic = getTopicBySlug(topicSlug);

  const activeIndex = topic
    ? Math.max(0, topic.sections.findIndex(s => s.slug === articleSlug || s.id === articleSlug))
    : 0;

  const activeSection = topic?.sections?.[activeIndex] || null;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeIndex]);

  // Track article read
  useEffect(() => {
    if (activeSection) {
      trackEvent('learn_article_read', { topic: topicSlug, article: activeSection.slug });
    }
  }, [topicSlug, activeSection]);

  const goToArticle = (sectionId) => {
    navigate(`/learn/${topicSlug}/${sectionId}`, { replace: true });
  };

  const goToIndex = (index) => {
    if (topic?.sections?.[index]) {
      goToArticle(topic.sections[index].slug || topic.sections[index].id);
    }
  };

  // Not found
  if (!topic) {
    return (
      <div style={{ minHeight: 'calc(100vh - 64px)', background: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', fontFamily: "'Montserrat', sans-serif" }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 12 }}>Topic Not Found</h1>
          <span
            role="button" tabIndex={0}
            onClick={() => navigate('/learn')}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/learn')}
            style={{ fontSize: 14, color: '#bb9749', fontWeight: 600, cursor: 'pointer', userSelect: 'none' }}
          >← Back to Learn</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: 'calc(100vh - 64px)', background: '#F9FAFB', fontFamily: "'Montserrat', sans-serif" }}>
      <style>{`
        .learn-article-content { color: #374151; font-size: 15px; line-height: 1.8; max-width: 720px; }
        .learn-article-content p { margin-bottom: 16px; }
        .learn-article-content h2 { font-size: 22px; font-weight: 700; color: #111827; margin-top: 32px; margin-bottom: 16px; }
        .learn-article-content h3 { font-size: 18px; font-weight: 600; color: #1f2937; margin-top: 28px; margin-bottom: 12px; }
        .learn-article-content strong { color: #1f2937; font-weight: 600; }
        .learn-article-content em { color: #6B7280; }
        .learn-article-content ul { margin-top: 8px; margin-bottom: 16px; padding-left: 24px; list-style-type: disc; }
        .learn-article-content ul li { margin-bottom: 8px; line-height: 1.7; }
        .learn-article-content ol { margin-top: 8px; margin-bottom: 16px; padding-left: 24px; list-style-type: decimal; }
        .learn-article-content ol li { margin-bottom: 8px; line-height: 1.7; }
        .learn-article-content a { color: #bb9749; text-decoration: none; font-weight: 500; }
        .learn-article-content a:hover { text-decoration: underline; }
      `}</style>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px 80px' }}>
        {/* Back nav */}
        <span
          role="button" tabIndex={0}
          onClick={() => navigate('/learn')}
          onKeyDown={(e) => e.key === 'Enter' && navigate('/learn')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#6B7280', cursor: 'pointer', userSelect: 'none', marginBottom: 24 }}
        >
          <ArrowLeft size={15} /> Back to Learn
        </span>

        {/* Topic header */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>{topic.icon}</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 6 }}>{topic.title}</h1>
          <p style={{ fontSize: 14, color: '#6B7280' }}>{topic.subtitle}</p>
        </div>

        {/* Mobile article selector */}
        {isMobile && topic.sections.length > 1 && (
          <div style={{ marginBottom: 24, overflowX: 'auto', whiteSpace: 'nowrap', paddingBottom: 8 }}>
            {topic.sections.map((s, i) => (
              <span
                key={s.id}
                role="button" tabIndex={0}
                onClick={() => goToArticle(s.slug || s.id)}
                onKeyDown={(e) => e.key === 'Enter' && goToArticle(s.slug || s.id)}
                style={{
                  display: 'inline-block', padding: '8px 14px', marginRight: 8, borderRadius: 8,
                  fontSize: 13, fontWeight: 500, cursor: 'pointer', userSelect: 'none',
                  whiteSpace: 'nowrap',
                  background: i === activeIndex ? '#FFF7ED' : '#FFFFFF',
                  border: i === activeIndex ? '1px solid #bb9749' : '1px solid #E5E7EB',
                  color: i === activeIndex ? '#bb9749' : '#6B7280',
                }}
              >
                {s.title.length > 35 ? s.title.slice(0, 35) + '...' : s.title}
              </span>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>
          {/* Desktop TOC */}
          {!isMobile && topic.sections.length > 1 && (
            <div style={{ width: 220, flexShrink: 0, position: 'sticky', top: 80 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Articles</p>
              {topic.sections.map((s, i) => {
                const isActive = i === activeIndex;
                return (
                  <span
                    key={s.id}
                    role="button" tabIndex={0}
                    onClick={() => goToArticle(s.slug || s.id)}
                    onKeyDown={(e) => e.key === 'Enter' && goToArticle(s.slug || s.id)}
                    style={{
                      display: 'block', padding: '8px 12px', marginBottom: 4, borderRadius: 6,
                      fontSize: 13, lineHeight: 1.4, cursor: 'pointer', userSelect: 'none',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? '#bb9749' : '#6B7280',
                      background: isActive ? '#FFF7ED' : 'transparent',
                      borderLeft: isActive ? '3px solid #bb9749' : '3px solid transparent',
                      transition: 'all 0.15s',
                    }}
                  >
                    {s.title}
                    <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                      {s.readTime || getReadTime(s.content)} min read
                    </span>
                  </span>
                );
              })}
            </div>
          )}

          {/* Article content */}
          <div style={{ flex: 1, maxWidth: 720 }}>
            {activeSection && (
              <div style={{ background: '#FFFFFF', borderRadius: 12, border: '1px solid #E5E7EB', padding: isMobile ? 20 : 32 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
                  {activeSection.title}
                </h2>

                {/* Video or placeholder */}
                {activeSection.videoUrl ? (
                  <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
                    <iframe
                      src={activeSection.videoUrl}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={`Video: ${activeSection.title}`}
                    />
                  </div>
                ) : activeSection.videoPlaceholder ? (
                  <VideoPlaceholder />
                ) : null}

                {/* Content */}
                <div
                  className="learn-article-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(activeSection.content) }}
                />

                {/* Prev / Next */}
                <div style={{ marginTop: 32, paddingTop: 20, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {activeIndex > 0 ? (
                    <span
                      role="button" tabIndex={0}
                      onClick={() => goToIndex(activeIndex - 1)}
                      onKeyDown={(e) => e.key === 'Enter' && goToIndex(activeIndex - 1)}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, color: '#6B7280', cursor: 'pointer', userSelect: 'none' }}
                    >
                      <ChevronLeft size={16} /> Previous article
                    </span>
                  ) : <div />}
                  {activeIndex < topic.sections.length - 1 ? (
                    <span
                      role="button" tabIndex={0}
                      onClick={() => goToIndex(activeIndex + 1)}
                      onKeyDown={(e) => e.key === 'Enter' && goToIndex(activeIndex + 1)}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, color: '#6B7280', cursor: 'pointer', userSelect: 'none' }}
                    >
                      Next article <ChevronRight size={16} />
                    </span>
                  ) : <div />}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnTopic;
